<template>
    <AppModal :name="name" :width="800" :beforeOpen="beforeOpen" :beforeClose="beforeClose">
        <template v-slot:header>
            Search documents
        </template>
        <template v-slot:content>

            <div class="row text-left">
                <div class="col-6"></div>
                <div class="col-6">
                    <AppSearchInput
                        :value="searchQuery"
                        @input="onSearchQueryChange($event.target.value)"
                        class="w-100"/>
                </div>
            </div>
            <div class="row justify-content-end mt-2 text-left">
                <div v-if="getSubsidiaries.length !== 0" class="col">
                    <div class="mb-1">
                        Subsidiary
                    </div>
                    <multiselect
                        @input="onSubsidiaryFilterChange"
                        :value="getSubsidiaries.find(l => l.uuid === extraFilter.subsidiaryId)"
                        :options="getSubsidiaries"
                        track-by="uuid"
                        label="name"
                        :placeholder="$lang.subsidiary.singular">
                    </multiselect>
                </div>
                <div v-if="getLocations.length !== 0" class="col">
                    <div class="mb-1">
                        Location
                    </div>
                    <multiselect
                        @input="onLocationFilterChange"
                        :value="getLocations.find(l => l.uuid === extraFilter.locationId)"
                        :options="getLocations"
                        track-by="uuid"
                        label="name"
                        :placeholder="$lang.location.singular">
                    </multiselect>
                </div>
                <div :class="getLocations.length !== 0 && getSubsidiaries.length !== 0 ? 'col-4' : 'col-6'">
                    <div class="mb-1">
                        Section
                    </div>
                    <multiselect
                        @input="onDocumentTypeFilterChange"
                        :value="availableDocumentTypes.find(l => l.type === extraFilter.docTypeKey)"
                        :options="availableDocumentTypes"
                        track-by="type"
                        label="name"
                        placeholder="Section">
                    </multiselect>
                </div>
            </div>

            <div class="row table-header-row">
                <div class="col-1 align-self-end"></div>
                <div class="col-2 align-self-end text-left pl-0">
                    Number
                </div>
                <div class="col-2 align-self-end text-left pl-0">
                    Type
                </div>
                <div class="col-2 align-self-end text-left pl-0">
                    Date
                </div>
                <div class="col-3 align-self-end text-left pl-0">
                    {{ $lang.supplier.singular }}
                </div>
                <div class="col-2 align-self-end text-right pr-0">
                    Total
                </div>
            </div>

            <hr class="my-0">

            <div class="docs-container">
                <div v-if="isDocsFetching" class="text-muted pt-2">
                    Loading...
                </div>
                <div v-else-if="docsList.length === 0" class="text-muted pt-2">
                    No results found
                </div>
                <div v-else style="height: 300px">
                    <DynamicScroller
                        key-field="uuid"
                        :items="docsList"
                        :min-item-size="41"
                        class="h-100 border-bottom">
                        <template v-slot="{ item, active }">
                            <DynamicScrollerItem
                                :item="item"
                                :active="active"
                                :size-dependencies="[item.company, item.number]"
                                :data-index="item.uuid">
                                <DocumentRow
                                    :doc="item"
                                    :isSelected="checkIsSelectedDoc(item)"
                                    @select="onDocSelect"/>
                            </DynamicScrollerItem>
                        </template>
                    </DynamicScroller>
                </div>
            </div>
        </template>

        <template v-slot:actions>
            <div class="d-flex align-items-center">
                <div class="flex-grow-1 text-left">

                    <slot name="footer-left"></slot>

                </div>
                <div>
                    <button @click="hideModal" class="btn btn-link mt-0">Cancel</button>
                    <button v-if="typeof onSelect === 'function'"
                            @click="confirmSelect"
                            :disabled="!hasSelectedDoc || isOnSelectPerforming"
                            class="btn btn-primary mt-0">
                        <span v-show="isOnSelectPerforming">
                            <i class="fas fa-spinner fa-pulse"></i>
                        </span>
                        <span v-show="!isOnSelectPerforming">
                            Select
                        </span>
                    </button>
                </div>
            </div>
        </template>
    </AppModal>
</template>

<script>
import axios from 'axios'
import {cloneDeep} from 'lodash'
import {mapGetters} from 'vuex'
import AppSearchInput from '../../../../components/shared/AppSearchInput'
import DocumentRow from './DocumentRow'
import {
    getDocTypeFilter,
    getLocationsFilter,
    getSearchStringQueryFilter,
    getSubsidiaryFilter
} from '@/views/documents/types/views/search'
import {searchDocuments} from '@/api/elasticsearch'
import {AppDocSystemTypes} from '@/types/document/types'

export default {
    name: 'SearchDocuments',
    props: {
        name: {
            type: String,
            required: true
        }
    },
    components: {DocumentRow, AppSearchInput},
    data() {
        return {
            selectedDoc: null,
            selectedDocsList: [],
            docsList: [],
            searchQuery: '',
            isDocsFetching: false,
            isOnSelectPerforming: false,
            extraFilter: {
                locationId: null,
                subsidiaryId: null,
                docTypeKey: null
            },

            multiple: false,
            onSelect: null,
            esFilter: []
        }
    },
    computed: {
        ...mapGetters([
            'activeTeamId',
            'userUUID',
            'getLocations',
            'getSubsidiaries'
        ]),

        ...mapGetters('documents', [
            'documentTypes'
        ]),

        hasSelectedDoc() {
            return this.multiple ? this.selectedDocsList.length !== 0 : this.selectedDoc
        },

        availableDocumentTypes() {
            return this.documentTypes.filter(d => [
                AppDocSystemTypes.SALES,
                AppDocSystemTypes.PURCHASING,
                AppDocSystemTypes.STATEMENTS,
                AppDocSystemTypes.BATCHES
            ].includes(d.type))
        }
    },
    methods: {
        async beforeOpen({params}) {
            if (params.esFilter) {
                this.esFilter = params.esFilter
            }
            if (params.onSelect) {
                this.onSelect = params.onSelect
            }
            if (params.multiple) {
                this.multiple = params.multiple
            }
            this.$watch(vm => [vm.searchQuery, vm.extraFilter], this.refreshDocument, {deep: true, immediate: true})
        },

        beforeClose() {
            Object.assign(this.$data, this.$options.data.apply(this))
        },

        onSearchQueryChange(query) {
            this.searchQuery = query
        },

        async refreshDocument() {
            this.isDocsFetching = true
            const filter = cloneDeep(this.esFilter)
            if (this.searchQuery) {
                filter.push(getSearchStringQueryFilter(this.searchQuery))
            }
            if (this.extraFilter.subsidiaryId) {
                const subsidiariesFilter = getSubsidiaryFilter([this.extraFilter.subsidiaryId])
                filter.push(subsidiariesFilter)
            }
            if (this.extraFilter.locationId) {
                const locationsFilter = getLocationsFilter([this.extraFilter.locationId])
                filter.push(locationsFilter)
            }
            if (this.extraFilter.docTypeKey) {
                const docTypeFilter = getDocTypeFilter(this.extraFilter.docTypeKey)
                filter.push(docTypeFilter)
            }
            const requestBody = {
                from: 0,
                size: 30,
                _source: ['uuid', 'status', 'section', 'number', 'type', 'dateInv', 'company', 'total'],
                query: {bool: {filter}}
            }
            const config = {requestId: 'fetch-docs'}
            try {
                const {hits} = await searchDocuments(requestBody, config)
                this.docsList = hits.hits.map(hit => hit._source)
                this.isDocsFetching = false
            } catch (e) {
                if (!axios.isCancel(e)) {
                    this.isDocsFetching = false
                }
            }
        },

        async confirmSelect() {
            if (typeof this.onSelect !== 'function') {
                return
            }
            this.isOnSelectPerforming = true
            if (this.multiple) {
                await this.onSelect(this.selectedDocsList)
            } else {
                await this.onSelect(this.selectedDoc)
            }
            this.isOnSelectPerforming = false
            this.hideModal()
        },

        onDocSelect(doc) {
            if (this.multiple) {
                if (this.checkIsSelectedDoc(doc)) {
                    this.selectedDocsList = this.selectedDocsList.filter(d => d.uuid !== doc.uuid)
                } else {
                    this.selectedDocsList.push(doc)
                }
            } else {
                this.selectedDoc = this.checkIsSelectedDoc(doc) ? null : doc
            }
        },

        checkIsSelectedDoc(doc) {
            if (this.multiple) {
                return this.selectedDocsList.some(d => d.uuid === doc.uuid)
            } else {
                return !!this.selectedDoc && this.selectedDoc.uuid === doc.uuid
            }
        },

        hideModal() {
            this.$modal.hide(this.name)
        },

        onSubsidiaryFilterChange(subsidiary) {
            this.$set(this.extraFilter, 'subsidiaryId', subsidiary ? subsidiary.uuid : null)
        },

        onLocationFilterChange(location) {
            this.$set(this.extraFilter, 'locationId', location ? location.uuid : null)
        },

        onDocumentTypeFilterChange(docType) {
            this.$set(this.extraFilter, 'docTypeKey', docType ? docType.type : null)
        }
    }
}
</script>

<style scoped>
button {
    width: 140px;
}

.docs-container {
    height: 300px;
    padding: 0;
}

.table-header-row {
    margin: 24px 0 6px 0;
}
</style>
